import { forwardRef, type CSSProperties, type HTMLAttributes, type ReactNode } from "react";
import { color, radius, space, elevation, chromeSurface, type ElevationLevel } from "@tokens/index";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

/**
 * Surface: the container primitive.
 *
 * Depth is tone and edge. A surface is a flat fill plus a hairline border.
 * Rung 0 sits on the canvas tone; rung 1 steps up one tone and adds a 1px
 * contact shadow. Shadow that reads as floating appears only at
 * `elevation >= 2`, which is reserved for the overlay primitives.
 *
 * Materials:
 *  - `solid`  surface fill and hairline border (default).
 *  - `tonal`  one more tonal step, no border.
 *  - `chrome` opaque fill one step above the canvas, no blur. Sticky and
 *             floating chrome only.
 *
 * The `elevation` strings ("none" | "low" | "medium") map onto the numeric
 * scale.
 */

export type SurfaceElevation = "none" | "low" | "medium" | ElevationLevel;
export type SurfaceMaterial = "solid" | "tonal" | "chrome";

export interface SurfaceProps extends HTMLAttributes<HTMLDivElement> {
    elevation?: SurfaceElevation;
    material?: SurfaceMaterial;
    /** Padding key from the space scale. Default `0`. */
    padding?: keyof typeof space;
    borderRadius?: keyof typeof radius;
    children: ReactNode;
    className?: string;
    as?: "div" | "article" | "section" | "main" | "aside" | "header" | "footer" | "nav";
}

function resolveElevationLevel(elev: SurfaceElevation): ElevationLevel {
    if (typeof elev === "number") return elev;
    switch (elev) {
        case "none":
            return 0;
        case "low":
            return 1;
        case "medium":
            return 2;
    }
}

function resolveBackground(level: ElevationLevel, material: SurfaceMaterial): string {
    if (material === "chrome") return chromeSurface.background;
    if (material === "tonal") return color.bg.surface2;
    if (level === 0) return color.bg.canvas;
    if (level <= 2) return color.bg.surface;
    return color.bg.surface2;
}

function resolveBorder(level: ElevationLevel, material: SurfaceMaterial): string {
    if (material === "tonal") return "none";
    return `1px solid ${elevation[level].border}`;
}

export const Surface = forwardRef<HTMLElement, SurfaceProps>(function Surface(
    {
        elevation: elevProp = "none",
        material = "solid",
        padding = 0,
        borderRadius = "none",
        as: Component = "div",
        className,
        children,
        style: styleOverride,
        ...rest
    },
    ref,
) {
    const level = resolveElevationLevel(elevProp);

    const computed: CSSProperties = {
        position: "relative",
        display: "block",
        padding: space[padding],
        borderRadius: radius[borderRadius],
        background: resolveBackground(level, material),
        border: resolveBorder(level, material),
        boxShadow: level >= 2 ? elevation[level].shadow : "none",
    };

    const style: CSSProperties =
        styleOverride !== undefined ? { ...computed, ...styleOverride } : computed;

    const Tag = Component as React.ElementType;

    return (
        <Tag
            ref={ref}
            data-elevation={level}
            data-material={material}
            style={style}
            className={cn(className)}
            {...rest}
        >
            {children}
        </Tag>
    );
});
